import Checkbox from "@mui/material/Checkbox";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import { GridRowOrderChangeParams } from "@mui/x-data-grid-pro/models/gridRowOrderChangeParams";
import { GridAlignment } from "@mui/x-data-grid/models/colDef/gridColDef";
import { GridRowModel } from "@mui/x-data-grid/models/gridRows";
import { GridRenderCellParams } from "@mui/x-data-grid/models/params/gridCellParams";
import { useFormik } from "formik";
import { MuiColorInput } from "mui-color-input";
import * as React from "react";
import { useEffect, useState } from "react";

import { Option } from "~ui-components/components/atoms/option";
import { LineCategoryConfigs } from "~ui-components/types/__generated/gql/graphql";

function updateRowPosition(
	initialIndex: number,
	newIndex: number,
	rows: GridRowModel[],
): GridRowModel[] {
	const rowsClone = [...rows];
	const [row] = rowsClone.splice(initialIndex, 1);
	if (row) rowsClone.splice(newIndex, 0, row);
	return rowsClone;
}

interface Props {
	form: ReturnType<typeof useFormik<Pick<LineCategoryConfigs, "symbolValues">>>;
	onRowsChange: (rows: GridRowModel[]) => void;
}

export const PolygonSortable = React.memo(({ form, onRowsChange }: Props) => {
	const {
		values: { symbolValues },
	} = form;
	const [rows, setRows] = useState<GridRowModel[]>([]);

	useEffect(() => {
		onRowsChange(rows);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rows]);

	useEffect(() => {
		const mappedRows = (symbolValues ?? []).map((symbolValue, index) => ({
			id: index,
			"fill-color": symbolValue?.symbol?.fill?.color ?? "",
			"fill-type": symbolValue?.symbol?.fill?.fillType ?? "",
			"category-label": symbolValue.label ?? "",
			"category-value": symbolValue.value,
			"legend-visibility": symbolValue.legendVisibility,
		}));
		setRows(mappedRows);
	}, [symbolValues]);

	const handleRowOrderChange = (params?: GridRowOrderChangeParams) => {
		if (!params) return;

		const newRows = updateRowPosition(
			params.oldIndex,
			params.targetIndex,
			rows,
		);
		setRows(newRows);
	};

	const ColorInputComponent = (params: GridRenderCellParams) => {
		const { id, value } = params;
		const [colorValue, setColorValue] = useState(value || "");
		return (
			<MuiColorInput
				sx={{ width: 200 }}
				format="hex"
				value={colorValue}
				onChange={(value) => setColorValue(value)}
				onBlur={() => {
					setRows((prevRows) =>
						prevRows.map((row) =>
							row.id === id ? { ...row, "fill-color": colorValue } : row,
						),
					);
				}}
			/>
		);
	};

	const CategoryLabelComponent = (params: GridRenderCellParams) => {
		const [labelValue, setLabelValue] = useState(params.value || "");
		return (
			<OutlinedInput
				value={labelValue}
				onChange={(event) => {
					setLabelValue(event.target.value);
				}}
				onBlur={() => {
					setRows((prevRows) =>
						prevRows.map((row) =>
							row.id === params.id
								? { ...row, "category-label": labelValue }
								: row,
						),
					);
				}}
				onKeyDown={(event) => {
					// Prevent the space key from causing the cell to lose focus
					if (
						event.key === " " ||
						event.key === "ArrowRight" ||
						event.key === "ArrowLeft"
					) {
						event.stopPropagation();
					}
				}}
				sx={{ width: "100%" }}
			/>
		);
	};

	const LegendVisibilityComponent = (params: GridRenderCellParams) => {
		const [visibility, setVisibility] = useState(params.value);
		return (
			<Checkbox
				sx={{ mr: 4 }}
				checked={visibility}
				onChange={(event) => {
					setVisibility(event.target.checked);
					setRows((prevRows) =>
						prevRows.map((row) =>
							row.id === params.id
								? { ...row, "legend-visibility": event.target.checked }
								: row,
						),
					);
				}}
			/>
		);
	};

	const FillTypeComponent = (params: GridRenderCellParams) => {
		const [type, setType] = useState(params.value);
		return (
			<Select
				onChange={(event) => {
					setType(event.target.value as string);
					setRows((prevRows) =>
						prevRows.map((row) =>
							row.id === params.id
								? { ...row, "fill-type": event.target.value as string }
								: row,
						),
					);
				}}
				value={type}>
				<Option value="solid">Solid</Option>
				<Option value="verticalline">Vertica Lline</Option>
				<Option value="horizontalline">Horizontal Line</Option>
				<Option value="slash">Slash</Option>
				<Option value="backslash">Back Slash</Option>
				<Option value="grid">Grid</Option>
				<Option value="diagonalgrid">Diagonal Grid</Option>
				<Option value="none">None</Option>
			</Select>
		);
	};

	const columns = React.useMemo(
		() => [
			{
				field: "fill-color",
				headerName: "Fill color",
				width: 200,
				renderCell: ColorInputComponent,
			},
			{
				field: "fill-type",
				headerName: "Fill style",
				width: 200,
				renderCell: FillTypeComponent,
			},
			{ field: "category-value", headerName: "Category value", width: 350 },
			{
				field: "category-label",
				width: 350,
				headerName: "Category label",
				renderCell: CategoryLabelComponent,
			},
			{
				field: "legend-visibility",
				headerName: "Legend visibility",
				width: 200,
				align: "right" as GridAlignment,
				headerAlign: "right" as GridAlignment,
				renderCell: LegendVisibilityComponent,
			},
		],
		[],
	);

	return (
		<div style={{ height: "calc(100dvh - 300px)", width: "100%" }}>
			<DataGridPro
				columns={columns}
				rows={rows}
				rowReordering
				onRowOrderChange={handleRowOrderChange}
				hideFooter
			/>
		</div>
	);
});
